// GENERADO — no editar a mano. Origen: backend/src/oficina/compartido/.
// Regenerar: node scripts/gen-oficina-compartido.mjs

// Estado visual de un contratado (docs/oficina-v2/DISENO-OFICINA-V2.html §2 y §8).
// Una sola función decide qué ve Pau en la escena, la lista, el Expediente y los avisos.
// Código PURO y sin imports salvo tipos del mismo directorio (se copia a la web).
import type { AgentStatus, Ciclo, ContratoEstado, EstadoVisual, Fase, Salud, Situacion } from './vocabulario.js';
export type { Situacion } from './vocabulario.js';

export interface EntradaEstadoVisual {
  ciclo: Ciclo;
  agentStatus: AgentStatus;
  fase: Fase;
  salud: Salud;
  preguntaAbierta: boolean;
  permisoPendiente: boolean;
  contratoEstado: ContratoEstado;
  sinCuota: boolean;
}

/**
 * Prioridad (de arriba abajo): llegando/saliendo > error > cierre > te_necesita > bloqueado > trabajando > esperando.
 *
 * 29-09 (Pau: «me dice que está parado por un problema y cuando entro no hay ningún problema; hablo con él»):
 * - Mientras la oficina cierra su trabajo, manda la oficina: lo que el agente declaró antes (una pregunta, un
 *   «bloqueado») ya no describe lo que pasa.
 * - Un «bloqueado» declarado por el agente es que espera algo de Pau (reglamento): «te necesita», no una avería.
 *   Solo mientras está parado; si ya trabaja, trabaja.
 * - Lo declarado que Pau ya contestó (le escribió después) no llega aquí: lo apaga repo.ts.
 * «bloqueado» queda para lo que de verdad le para sin depender de Pau: sin cupo, salud rojo, atascado.
 */
export function estadoVisual(x: EntradaEstadoVisual): EstadoVisual {
  if (x.ciclo === 'llegando') return 'llegando';
  if (x.ciclo === 'cerrado' || x.ciclo === 'despedido') return 'saliendo';
  if (x.agentStatus === 'error') return 'error';
  if (x.ciclo === 'cierre_atascado') return 'te_necesita';
  // En el cierre, el único permiso posible es el «¿lo integro?» que crea la oficina.
  if (x.ciclo === 'cerrando') return x.permisoPendiente ? 'te_necesita' : 'cerrando';
  if (x.preguntaAbierta || x.permisoPendiente) return 'te_necesita';
  if (x.contratoEstado === 'propuesto') return 'te_necesita';
  if (x.fase === 'bloqueado' && x.agentStatus !== 'running') return 'te_necesita';
  if (x.sinCuota || x.salud === 'rojo' || x.agentStatus === 'stuck') return 'bloqueado';
  if (x.agentStatus === 'running') return 'trabajando';
  return 'esperando';
}

/** Qué necesita de Pau un contratado en «te_necesita» (S10: la lista del equipo lo dice en una línea).
 *  Mismo orden que estadoVisual; null si su estado visual no es «te_necesita». */
export type MotivoTeNecesita = 'atasco' | 'pregunta' | 'integrar' | 'permiso' | 'plan' | 'bloqueo';
export function motivoTeNecesita(x: EntradaEstadoVisual): MotivoTeNecesita | null {
  if (estadoVisual(x) !== 'te_necesita') return null;
  if (x.ciclo === 'cierre_atascado') return 'atasco';
  if (x.ciclo === 'cerrando') return 'integrar';
  if (x.preguntaAbierta) return 'pregunta';
  if (x.permisoPendiente) return 'permiso';
  if (x.contratoEstado === 'propuesto') return 'plan';
  return 'bloqueo';
}

/** «Sin noticias»: trabajando y más de 15 min sin informar. Nunca significa «terminado». */
export const MINUTOS_SIN_NOTICIAS = 15;
export function sinNoticias(agentStatus: AgentStatus, ultimoInformeEn: string | null, ahoraMs: number): boolean {
  if (agentStatus !== 'running' || !ultimoInformeEn) return false;
  const t = Date.parse(ultimoInformeEn);
  return Number.isFinite(t) && ahoraMs - t > MINUTOS_SIN_NOTICIAS * 60_000;
}

/**
 * S12 (Pau 29-09: «no me queda claro cuándo trabajan, cuándo esperan mi respuesta, cuándo me piden algo para
 * seguir de verdad o una autorización»). LA respuesta a «¿qué me toca?», de mayor a menor peso. Ver
 * docs/oficina-v2/S12-ESTADOS-BRIEF.md. Lo declarado por el agente llega aquí ya filtrado por vigencia (repo.ts).
 */

export interface EntradaSituacion {
  ciclo: Ciclo;
  agentStatus: AgentStatus;
  permisoPendiente: boolean;
  /** Pregunta con bloquea=true, vigente. */
  preguntaBloquea: boolean;
  /** «bloqueado» declarado, vigente. */
  bloqueoDeclarado: boolean;
  /** Pregunta que no bloquea o plan propuesto, vigentes. */
  preguntaOPlan: boolean;
  /** Parado y su último mensaje es posterior a la última palabra de Pau. */
  teEscribio: boolean;
  sinCuota: boolean;
  /** Mensajes suyos o para él que no han llegado. */
  sinEntregar: boolean;
  /** 30-09 — dice que trabaja pero se ha dormido y el despertador no lo ha levantado (despertador.ts). */
  dormido?: boolean;
  /** 30-09 — parado esperando algo que corre solo (despertador, o solo informó a Pau): va con los que trabajan. */
  esperaProceso?: boolean;
}

export function situacion(x: EntradaSituacion): Situacion {
  if (x.ciclo === 'llegando') return 'llegando';
  if (x.ciclo === 'cerrado' || x.ciclo === 'despedido') return 'saliendo';
  if (x.permisoPendiente) return 'autorizacion';
  if (x.ciclo === 'cierre_atascado') return 'bloqueado';
  if (x.ciclo === 'cerrando') return 'cerrando';
  if (x.agentStatus === 'error' || x.sinCuota || x.sinEntregar || x.dormido) return 'problema';
  const trabajando = x.agentStatus === 'running' || x.agentStatus === 'stuck';
  if (x.preguntaBloquea || (x.bloqueoDeclarado && !trabajando)) return 'bloqueado';
  if (trabajando) return 'trabajando';
  if (x.preguntaOPlan || x.teEscribio) return 'te_escribio';
  if (x.esperaProceso) return 'trabajando';
  return 'sin_pendiente';
}

/** Lo que va primero en la lista (grupos, de arriba abajo). */
export const ORDEN_SITUACION: readonly Situacion[] = ['autorizacion', 'bloqueado', 'te_escribio', 'problema', 'trabajando', 'cerrando', 'llegando', 'sin_pendiente', 'saliendo'];
